<script setup>
import Logo from '@/components/icons/Logo.vue';
import { useLayout } from '@/layout/common/composables/layout';
import { useMenuItems } from '@/layout/main/menu';
import Sidebar from '@/layout/main/sidebar';
import { useRoute, useRouter } from 'vue-router';
import { ref, computed } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useNotifications, isPermissionError} from '@/utils/toast.js';
import { useLanguageStore } from '@/stores/language';
import { storeToRefs } from 'pinia';
import moiVerticalLogo from '@/assets/images/logo/moi-vertical-logo.png';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const toast = useNotifications();
const languageStore = useLanguageStore();
const { t } = languageStore;
const { isRtl } = storeToRefs(languageStore);
const { menuItems, bottomMenuItems: bottomMenu } = useMenuItems();
const isLoggingOut = ref(false);

const { calculatedMenuMode, menuState, layoutConfig } = useLayout();

// Track open/close state for sub-collapsible items (3rd level nesting)
const openSubMenus = ref({});
const toggleSubMenu = (key) => {
    openSubMenus.value[key] = !openSubMenus.value[key];
};
const isSubMenuOpen = (key) => !!openSubMenus.value[key];

// Handle logout functionality
const handleLogout = async () => {
    if (isLoggingOut.value) return;
    
    isLoggingOut.value = true;
    try {
        const response = await authStore.logout();
        toast.success(t('You have been logged out successfully'), t('Logout Successful'));

        // Complete the Keycloak session logout when the backend provides it.
        const ssoLogoutUrl = response?.data?.logout_url;
        if (ssoLogoutUrl) {
            window.location.assign(ssoLogoutUrl);
            return;
        }
        
        // Redirect to login page
        router.push('/auth/login');
    } catch (error) {
        console.error('Logout error:', error);
        if (!isPermissionError(error)) {
          toast.error(t('Failed to logout. Please try again.'), t('Logout Error'));
        }
    } finally {
        isLoggingOut.value = false;
    }
};

// Determine if sidebar is in collapsed state
const isCollapsed = computed(() => {
    return menuState.value === 'collapsed' || 
           calculatedMenuMode.value === 'compact' || 
           calculatedMenuMode.value === 'slim' ||
           calculatedMenuMode.value === 'drawer' ||
           calculatedMenuMode.value === 'reveal' ||
           (calculatedMenuMode.value === 'drawer' && menuState.value === 'collapsed') ||
           (calculatedMenuMode.value === 'reveal' && menuState.value === 'collapsed');
});

// Determine if we should show favicon (for compact menu modes or collapsed state)
const shouldShowFavicon = computed(() => {
    // For drawer and reveal modes, always show favicon when the menu is not expanded
    if (calculatedMenuMode.value === 'drawer' || calculatedMenuMode.value === 'reveal') {
        return menuState.value !== 'expanded';
    }
    
    // For compact and slim modes, always show favicon
    if (calculatedMenuMode.value === 'compact' || calculatedMenuMode.value === 'slim') {
        return true;
    }
    
    // For static and overlay modes, check the collapsed state
    return menuState.value === 'collapsed';
});
</script>

<template>
    <Sidebar class="moi-navigation">
        <!-- Header -->
        <Sidebar.Header class="moi-sidebar-header flex items-center justify-center">
            <RouterLink 
                to="/" 
                class="flex items-center text-white dark:text-surface-100 background-transparent"
                :class="{
                    'gap-2': !shouldShowFavicon,
                    'w-full justify-center': shouldShowFavicon
                }"
                exact-active-class=""
            >
                <div 
                    class="flex items-center justify-center overflow-hidden"
                    :class="{
                        'w-full': shouldShowFavicon,
                        'min-w-0': !shouldShowFavicon
                    }"
                >
                    <!-- Show favicon when collapsed or in compact menu modes -->
                    <img 
                        v-if="shouldShowFavicon"
                        :src="moiVerticalLogo"
                        :alt="t('Ministry of Interior Medical Services')"
                        width="42"
                        height="42"
                        class="moi-sidebar-symbol object-contain mx-auto"
                    />
                    <!-- Show full logo when expanded -->
                    <Logo 
                        v-else
                        :width="calculatedMenuMode === 'horizontal' ? 110 : 186"
                        :height="calculatedMenuMode === 'horizontal' ? 46 : 66"
                        class="moi-sidebar-logo max-w-full"
                    />
                </div>
            </RouterLink>
        </Sidebar.Header>

        <div v-if="!shouldShowFavicon && calculatedMenuMode !== 'horizontal'" class="moi-sidebar-divider" aria-hidden="true"></div>

        <div v-if="!shouldShowFavicon && calculatedMenuMode !== 'horizontal'" class="moi-sidebar-section-label">
            {{ t('Workspace') }}
        </div>
        
        <!-- Main Content Area with proper flex layout -->
        <div
            class="flex-1 flex flex-col min-h-0 overflow-hidden"
            :class="{ 'moi-horizontal-body': calculatedMenuMode === 'horizontal' }"
        >
            <!-- Scrollable Menu Content - Takes remaining space -->
            <div
                class="moi-menu-scroll flex-1 overflow-y-auto overflow-x-hidden"
                :class="{
                    'overflow-x-auto overflow-y-hidden': calculatedMenuMode === 'horizontal',
                }"
            >
                <!-- Content -->
                <Sidebar.Content v-if="calculatedMenuMode !== 'slim' && calculatedMenuMode !== 'compact' && calculatedMenuMode !== 'horizontal'">
                    <Sidebar.Group>
                        <Sidebar.GroupContent>
                            <Sidebar.Menu>
                                <Sidebar.MenuItem v-for="item in menuItems" :key="item.label" :collapsible="!!item.items">
                                    <Sidebar.MenuButton :href="item.url" :target="item.target" :to="item.to" :is-active="route.path === item.to" :is-first="true">
                                        <i :class="item.icon" class="!leading-none !text-sm" />
                                        <span>{{ item.label }}</span>
                                    </Sidebar.MenuButton>
                                    <Sidebar.Menu v-if="item.items">
                                        <template v-for="subItem in item.items" :key="subItem.label">
                                            <!-- Sub-item WITHOUT children (normal link) -->
                                            <Sidebar.MenuItem v-if="!subItem.items">
                                                <Sidebar.MenuButton :href="subItem.url" :target="subItem.target" :to="subItem.to" :is-active="route.path === subItem.to">
                                                    <i :class="subItem.icon" class="!leading-none !text-sm" />
                                                    <span>{{ subItem.label }}</span>
                                                </Sidebar.MenuButton>
                                            </Sidebar.MenuItem>
                                            <!-- Sub-item WITH children: toggle header -->
                                            <Sidebar.MenuItem v-else>
                                                <button 
                                                    @click="toggleSubMenu(subItem.label)"
                                                    class="group inline-flex w-full items-center gap-3 rounded-xl px-4 py-2 font-medium text-white dark:text-surface-100 hover:bg-primary-500 hover:bg-opacity-50 dark:hover:bg-surface-700 transition-all duration-200"
                                                    :class="{ 'bg-surface-200/75 dark:bg-surface-800/75': isSubMenuOpen(subItem.label) }"
                                                >
                                                    <i :class="subItem.icon" class="!leading-none !text-sm text-white dark:text-surface-100" />
                                                    <span class="whitespace-nowrap truncate text-white dark:text-surface-100">{{ subItem.label }}</span>
                                                    <span class="ml-auto">
                                                        <i class="pi pi-chevron-down transition-transform !text-xs text-white dark:text-surface-100" :class="{ 'rotate-180': isSubMenuOpen(subItem.label) }" />
                                                    </span>
                                                </button>
                                            </Sidebar.MenuItem>
                                            <!-- Sub-item WITH children: the child links rendered as siblings -->
                                            <template v-if="subItem.items && isSubMenuOpen(subItem.label)">
                                                <Sidebar.MenuItem v-for="deepItem in subItem.items" :key="deepItem.label">
                                                    <Sidebar.MenuButton :href="deepItem.url" :target="deepItem.target" :to="deepItem.to" :is-active="route.path === deepItem.to" :class="isRtl ? 'pr-8' : 'pl-8'">
                                                        <i :class="deepItem.icon" class="!leading-none !text-sm" />
                                                        <span>{{ deepItem.label }}</span>
                                                    </Sidebar.MenuButton>
                                                </Sidebar.MenuItem>
                                            </template>
                                        </template>
                                    </Sidebar.Menu>
                                </Sidebar.MenuItem>
                            </Sidebar.Menu>
                        </Sidebar.GroupContent>
                    </Sidebar.Group>
                </Sidebar.Content>
                <Sidebar.Content v-else class="moi-horizontal-content">
                    <Sidebar.Group>
                        <Sidebar.GroupContent>
                            <Sidebar.Menu>
                                <Sidebar.MenuItem v-for="item in menuItems" :key="item.label">
                                    <Sidebar.Popover v-if="item.items" :placement="calculatedMenuMode === 'horizontal' ? 'bottom' : 'right'">
                                        <template #trigger>
                                            <Sidebar.MenuButton :href="item.url" :target="item.target" :to="item.to" :isActive="route.path === item.to" :tooltip-value="calculatedMenuMode === 'compact' ? item.label : ''">
                                                <i :class="item.icon" class="!leading-none !text-sm text-white" />
                                                <span class="text-white">{{ item.label }}</span>
                                            </Sidebar.MenuButton>
                                        </template>
                                        <template #content>
                                            <Sidebar.Menu mode="static">
                                                <Sidebar.MenuItem v-for="subItem in item.items" :key="subItem.label" :collapsible="!!subItem.items">
                                                    <Sidebar.MenuButton mode="static" :href="subItem.url" :target="subItem.target" :to="subItem.to" :isActive="route.path === subItem.to">
                                                        <i :class="subItem.icon" class="!leading-none !text-sm text-white" />
                                                        <span class="text-white">{{ subItem.label }}</span>
                                                    </Sidebar.MenuButton>
                                                    <Sidebar.Menu mode="static" v-if="subItem.items">
                                                        <Sidebar.MenuItem v-for="deepItem in subItem.items" :key="deepItem.label">
                                                            <Sidebar.MenuButton mode="static" :href="deepItem.url" :target="deepItem.target" :to="deepItem.to" :isActive="route.path === deepItem.to">
                                                                <i :class="deepItem.icon" class="!leading-none !text-sm text-white" />
                                                                <span class="text-white">{{ deepItem.label }}</span>
                                                            </Sidebar.MenuButton>
                                                        </Sidebar.MenuItem>
                                                    </Sidebar.Menu>
                                                </Sidebar.MenuItem>
                                            </Sidebar.Menu>
                                        </template>
                                    </Sidebar.Popover>
                                    <Sidebar.MenuButton v-else :href="item.url" :target="item.target" :to="item.to" :isActive="route.path === item.to" :tooltip-value="calculatedMenuMode === 'compact' ? item.label : ''">
                                        <i :class="item.icon" class="!leading-none !text-sm text-white" />
                                        <span class="text-white">{{ item.label }}</span>
                                    </Sidebar.MenuButton>
                                </Sidebar.MenuItem>
                            </Sidebar.Menu>
                        </Sidebar.GroupContent>
                    </Sidebar.Group>
                </Sidebar.Content>
            </div>
            
            <!-- Bottom Menu Section - Fixed at bottom -->
            <div
                class="moi-sidebar-footer shrink-0 flex flex-col"
                :dir="isRtl ? 'rtl' : 'ltr'"
                :class="{
                    'moi-sidebar-footer--ltr': !isRtl,
                    'moi-sidebar-footer--rtl': isRtl,
                    'mx-4': (calculatedMenuMode === 'static' || calculatedMenuMode === 'overlay') || 
                           ((calculatedMenuMode === 'drawer' || calculatedMenuMode === 'reveal') && layoutConfig.menuState === 'expanded'),
                    'mx-2': calculatedMenuMode === 'slim' || calculatedMenuMode === 'compact' || 
                           ((calculatedMenuMode === 'drawer' || calculatedMenuMode === 'reveal') && layoutConfig.menuState === 'collapsed'),
                    'mx-0': calculatedMenuMode === 'horizontal'
                }"
            >
                <!-- Scrollable bottom menu container with max height -->
                <div class="overflow-y-auto overflow-x-hidden" style="max-height: calc(100vh - 400px);">
                    <div class="pt-4 pb-4">
                        <Sidebar.Menu>
                            <Sidebar.MenuItem v-for="item in bottomMenu" :key="item.label" :collapsible="!!item.items">
                                <!-- For regular sidebar modes (static, overlay, drawer, reveal) -->
                                <template v-if="calculatedMenuMode === 'static' || calculatedMenuMode === 'overlay' || calculatedMenuMode === 'drawer' || calculatedMenuMode === 'reveal'">
                                    <Sidebar.MenuButton 
                                        v-if="item.action === 'logout'"
                                        @click="handleLogout"
                                        :disabled="isLoggingOut"
                                        :is-first="true"
                                        class="cursor-pointer"
                                    >
                                        <i :class="item.icon" class="!leading-none !text-sm" />
                                        <span>{{ isLoggingOut ? t('Logging out...') : item.label }}</span>
                                    </Sidebar.MenuButton>
                                    <Sidebar.MenuButton 
                                        v-else
                                        :href="item.url" 
                                        :target="item.target" 
                                        :to="item.to" 
                                        :is-active="route.path === item.to"
                                        :is-first="true"
                                    >
                                        <i :class="item.icon" class="!leading-none !text-sm" />
                                        <span>{{ item.label }}</span>
                                    </Sidebar.MenuButton>
                                    <!-- Submenu for items with subitems -->
                                    <Sidebar.Menu v-if="item.items">
                                        <Sidebar.MenuItem v-for="subItem in item.items" :key="subItem.label">
                                            <Sidebar.MenuButton :href="subItem.url" :target="subItem.target" :to="subItem.to" :is-active="route.path === subItem.to">
                                                <i :class="subItem.icon" class="!leading-none !text-sm" />
                                                <span>{{ subItem.label }}</span>
                                            </Sidebar.MenuButton>
                                        </Sidebar.MenuItem>
                                    </Sidebar.Menu>
                                </template>
                                
                                <!-- For compact/slim/horizontal modes with popover -->
                                <template v-else>
                                    <!-- Only use Popover for items that have subitems -->
                                    <template v-if="item.items && item.items.length > 0">
                                        <Sidebar.Popover :placement="calculatedMenuMode === 'horizontal' ? 'top' : 'right'">
                                            <template #trigger>
                                                <Sidebar.MenuButton 
                                                    :href="item.url" 
                                                    :target="item.target" 
                                                    :to="item.to" 
                                                    :is-active="route.path === item.to"
                                                    :tooltip-value="(calculatedMenuMode === 'compact' || calculatedMenuMode === 'slim') ? item.label : ''"
                                                >
                                                    <i :class="item.icon" class="!leading-none !text-sm" />
                                                    <span>{{ item.label }}</span>
                                                </Sidebar.MenuButton>
                                            </template>
                                            <template #content>
                                                <Sidebar.Menu mode="static">
                                                    <Sidebar.MenuItem v-for="subItem in item.items" :key="subItem.label">
                                                        <Sidebar.MenuButton mode="static" :href="subItem.url" :target="subItem.target" :to="subItem.to" :isActive="route.path === subItem.to">
                                                            <i :class="subItem.icon" class="!leading-none !text-sm" />
                                                            <span>{{ subItem.label }}</span>
                                                        </Sidebar.MenuButton>
                                                    </Sidebar.MenuItem>
                                                </Sidebar.Menu>
                                            </template>
                                        </Sidebar.Popover>
                                    </template>
                                    <!-- Regular button for items without subitems -->
                                    <template v-else>
                                        <Sidebar.MenuButton 
                                            v-if="item.action === 'logout'"
                                            @click="handleLogout"
                                            :disabled="isLoggingOut"
                                            :tooltip-value="(calculatedMenuMode === 'compact' || calculatedMenuMode === 'slim') ? (isLoggingOut ? t('Logging out...') : item.label) : ''"
                                            class="cursor-pointer"
                                        >
                                            <i :class="item.icon" class="!leading-none !text-sm" />
                                            <span>{{ isLoggingOut ? t('Logging out...') : item.label }}</span>
                                        </Sidebar.MenuButton>
                                        <Sidebar.MenuButton 
                                            v-else
                                            :href="item.url" 
                                            :target="item.target" 
                                            :to="item.to" 
                                            :is-active="route.path === item.to"
                                            :tooltip-value="(calculatedMenuMode === 'compact' || calculatedMenuMode === 'slim') ? item.label : ''"
                                        >
                                            <i :class="item.icon" class="!leading-none !text-sm" />
                                            <span>{{ item.label }}</span>
                                        </Sidebar.MenuButton>
                                    </template>
                                </template>
                            </Sidebar.MenuItem>
                        </Sidebar.Menu>
                    </div>
                </div>
                <!-- End of scrollable bottom menu -->
            </div>
            <!-- End of bottom menu section -->
        </div>
        <!-- End of main content area -->
    </Sidebar>
</template>

<style scoped>
:deep(.moi-navigation) {
    padding: .65rem;
}

.moi-sidebar-header {
    min-height: 5.75rem;
    padding: 1rem 1rem .5rem;
}

.moi-sidebar-logo {
    filter: saturate(.96) contrast(1.02);
}

.moi-sidebar-symbol {
    width: 2.65rem;
    height: 2.65rem;
    padding: .3rem;
    border: 1px solid rgba(34, 169, 174, .12);
    border-radius: .85rem;
    background: linear-gradient(145deg, #fff, var(--sfh-teal-soft, #e8f7f7));
    box-shadow: 0 7px 18px rgba(22, 140, 145, .1);
}

.moi-sidebar-divider {
    height: 1px;
    margin: .25rem 1rem 1rem;
    background: linear-gradient(90deg, transparent, var(--sfh-border-subtle, rgba(0, 0, 0, .08)) 14%, var(--sfh-border-subtle, rgba(0, 0, 0, .08)) 86%, transparent);
}

.moi-sidebar-section-label {
    margin: 0 1rem .45rem;
    color: var(--sfh-text-muted, #929292);
    font-size: .62rem;
    font-weight: 800;
    letter-spacing: .12em;
    text-transform: uppercase;
}

.moi-sidebar-footer {
    position: relative;
    margin-top: .4rem;
    border-top: 1px solid var(--sfh-border-subtle, rgba(0, 0, 0, .06));
}

/* Keep footer actions anchored to the language's reading edge. */
.moi-sidebar-footer--ltr {
    direction: ltr;
    text-align: left;
}

.moi-sidebar-footer--rtl {
    direction: rtl;
    text-align: right;
}

:deep(.moi-sidebar-footer--rtl [data-sidebar='menu']) {
    width: 100%;
    align-items: stretch;
    direction: rtl;
}

:deep(.moi-sidebar-footer--rtl [data-sidebar='menu'] > li) {
    width: 100%;
    text-align: right;
}

:deep(.moi-sidebar-footer--ltr [data-sidebar='menu']) {
    align-items: stretch;
    direction: ltr;
}

:deep(.moi-sidebar-footer--ltr [data-sidebar='menu'] > li) {
    text-align: left;
}

:deep(.moi-sidebar-footer--ltr [data-sidebar='menu'] > li > [data-active][data-variant='static']),
:deep(.moi-sidebar-footer--ltr [data-sidebar='menu'] > li > [data-active][data-variant='overlay']),
:deep(.moi-sidebar-footer--ltr [data-sidebar='menu'] > li > [data-active][data-variant='drawer'][data-state='expanded']),
:deep(.moi-sidebar-footer--ltr [data-sidebar='menu'] > li > [data-active][data-variant='reveal'][data-state='expanded']) {
    flex-direction: row !important;
    justify-content: flex-start !important;
    text-align: left !important;
}

:deep(.moi-sidebar-footer--ltr [data-sidebar='menu'] > li > [data-active] > span) {
    text-align: left !important;
}

:deep(.moi-sidebar-footer--rtl [data-sidebar='menu'] > li > [data-active][data-variant='static']),
:deep(.moi-sidebar-footer--rtl [data-sidebar='menu'] > li > [data-active][data-variant='overlay']),
:deep(.moi-sidebar-footer--rtl [data-sidebar='menu'] > li > [data-active][data-variant='drawer'][data-state='expanded']),
:deep(.moi-sidebar-footer--rtl [data-sidebar='menu'] > li > [data-active][data-variant='reveal'][data-state='expanded']) {
    display: grid !important;
    width: 100%;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    justify-content: stretch !important;
    direction: rtl !important;
    text-align: right !important;
}

:deep(.moi-sidebar-footer--rtl [data-sidebar='menu'] > li > [data-active] > span) {
    min-width: 0;
    justify-self: start;
    direction: rtl;
    text-align: right !important;
}

:deep(.moi-navigation [data-active='false']) {
    color: var(--sfh-text-secondary, #666) !important;
}

:deep(.moi-navigation [data-active='false'] i),
:deep(.moi-navigation [data-active='false'] span) {
    color: inherit !important;
}

:deep(.moi-navigation [data-active='false']:hover) {
    color: var(--sfh-teal-dark, #168c91) !important;
}

:deep(.moi-navigation [data-active='true']) {
    box-shadow: inset 0 0 0 1px rgba(34, 169, 174, .07);
}

/* Horizontal mode is a compact top navigation bar. Primary items can scroll
   on narrower desktop widths without increasing the bar height. */
:deep(.moi-navigation[data-variant='horizontal']) {
    width: 100%;
    height: 4.75rem;
    min-height: 4.75rem;
    max-height: 4.75rem;
    padding: .45rem clamp(.75rem, 1.6vw, 1.35rem);
    align-items: center;
    gap: .75rem;
    overflow: visible;
}

:deep(.moi-navigation[data-variant='horizontal'] .moi-sidebar-header) {
    width: clamp(7rem, 10vw, 9rem);
    min-width: 7rem;
    min-height: 3.75rem;
    padding: 0 .5rem;
    flex: 0 0 auto;
}

.moi-horizontal-body {
    min-width: 0;
    height: 3.75rem;
    flex-direction: row !important;
    align-items: center;
    overflow: visible !important;
}

.moi-horizontal-body .moi-menu-scroll {
    min-width: 0;
    height: 3.75rem;
    display: flex;
    align-items: center;
    overflow-x: auto !important;
    overflow-y: hidden !important;
    overscroll-behavior-inline: contain;
    scrollbar-width: none;
}

.moi-horizontal-body .moi-menu-scroll::-webkit-scrollbar { display: none; }

:deep(.moi-navigation[data-variant='horizontal'] .moi-horizontal-content) {
    width: max-content;
    min-width: max-content;
    flex: 0 0 auto;
    flex-direction: row !important;
    align-items: center;
}

:deep(.moi-navigation[data-variant='horizontal'] .moi-horizontal-content > div),
:deep(.moi-navigation[data-variant='horizontal'] .moi-horizontal-content > div > div) {
    width: max-content;
    min-width: max-content;
    flex: 0 0 auto;
    padding-block: 0 !important;
}

:deep(.moi-navigation[data-variant='horizontal'] .moi-horizontal-content [data-sidebar='menu']) {
    display: grid !important;
    width: max-content;
    min-width: max-content;
    flex: 0 0 auto;
    grid-auto-flow: column;
    grid-auto-columns: max-content;
    align-items: center;
    justify-content: start;
    gap: clamp(.1rem, .35vw, .4rem);
}

:deep(.moi-navigation[data-variant='horizontal'] .moi-horizontal-content [data-sidebar='menu'] > li) {
    min-width: max-content;
    width: max-content;
}

:deep(.moi-navigation[data-variant='horizontal'] .moi-horizontal-content [data-variant='horizontal']) {
    width: auto;
    min-height: 2.75rem;
    padding-inline: clamp(.42rem, .6vw, .72rem);
    gap: clamp(.32rem, .45vw, .6rem);
    font-size: clamp(.68rem, .72vw, .8rem);
}

:deep(.moi-navigation[data-variant='horizontal'] .moi-horizontal-content [data-variant='horizontal'] > span) {
    min-width: 0;
    font-size: inherit;
}

:deep(.moi-navigation[data-variant='horizontal'] .moi-horizontal-content [data-variant='horizontal'] > span:first-of-type) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.moi-horizontal-body .moi-sidebar-footer {
    width: max-content;
    min-width: max-content;
    height: 3.25rem;
    margin: 0 0 0 .55rem;
    padding-inline-start: .55rem;
    flex: 0 0 auto;
    flex-direction: row !important;
    align-items: center;
    border-top: 0;
    border-inline-start: 1px solid var(--sfh-border-subtle, rgba(0, 0, 0, .06));
}

:deep(.moi-navigation[data-variant='horizontal'] .moi-sidebar-footer [data-sidebar='menu']) {
    display: grid !important;
    width: max-content !important;
    min-width: max-content !important;
    grid-auto-flow: column;
    grid-auto-columns: max-content;
    align-items: center;
}

:deep(.moi-navigation[data-variant='horizontal'] .moi-sidebar-footer [data-sidebar='menu'] > li) {
    flex: 0 0 auto;
}

:global(html[dir='rtl']) .moi-horizontal-body .moi-sidebar-footer {
    margin: 0 .55rem 0 0;
}

.moi-horizontal-body .moi-sidebar-footer > div,
.moi-horizontal-body .moi-sidebar-footer > div > div {
    display: flex;
    max-height: none !important;
    padding-block: 0 !important;
    overflow: visible !important;
}

@media (max-width: 1180px) {
    :deep(.moi-navigation[data-variant='horizontal'] .moi-sidebar-header) {
        width: 6.5rem;
        min-width: 6.5rem;
    }

    :deep(.moi-navigation[data-variant='horizontal'] .moi-horizontal-content [data-variant='horizontal']) {
        padding-inline: .65rem;
    }

    :deep(.moi-navigation[data-variant='horizontal'] .moi-sidebar-footer [data-variant='horizontal'] > span:first-of-type) {
        display: none;
    }
}

@media (max-height: 760px) {
    .moi-sidebar-header { min-height: 4.75rem; }
}
</style>
